import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faNewspaper,
  faSync,
  faSpinner,
  faExternalLinkAlt,
  faFilter,
  faTimes,
  faBrain,
  faChevronDown,
  faChevronUp,
  faFileAlt,
  faQuoteLeft,
} from '@fortawesome/free-solid-svg-icons';
import { newsApi } from '../api/newsApi';
import { marketDataApi } from '../api/marketDataApi';

function formatAbsolute(dateString) {
  if (!dateString) return '—';
  return new Date(dateString).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatRelative(dateString) {
  if (!dateString) return '';
  const ms = Date.now() - new Date(dateString).getTime();
  if (!Number.isFinite(ms) || ms < 0) return formatAbsolute(dateString);
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return formatAbsolute(dateString);
}

function sentimentTone(sentiment) {
  switch (String(sentiment || '').toLowerCase()) {
    case 'positive':
      return {
        label: 'Bullish',
        chip: 'bg-[var(--lt-bull-dim)] text-[var(--lt-bull)] border-[rgba(61,220,151,0.35)]',
        bar: 'bg-[var(--lt-bull)]',
      };
    case 'negative':
      return {
        label: 'Bearish',
        chip: 'bg-[var(--lt-bear-dim)] text-[var(--lt-bear)] border-[rgba(240,113,103,0.35)]',
        bar: 'bg-[var(--lt-bear)]',
      };
    case 'neutral':
      return {
        label: 'Neutral',
        chip: 'bg-[var(--lt-hover)] text-[var(--lt-muted)] border-[var(--lt-border)]',
        bar: 'bg-[var(--lt-faint)]',
      };
    default:
      return {
        label: 'Unscored',
        chip: 'bg-[var(--lt-hover)] text-[var(--lt-faint)] border-[var(--lt-border)]',
        bar: 'bg-[var(--lt-border-strong)]',
      };
  }
}

function formatPrice(n) {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  return `$${Number(n).toFixed(2)}`;
}

const NewsPage = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState(null);
  const [statusMsg, setStatusMsg] = useState(null);
  const [stats, setStats] = useState(null);
  const [expandedArticles, setExpandedArticles] = useState(new Set());
  const [summarizingArticles, setSummarizingArticles] = useState(new Set());
  const [articleSummaries, setArticleSummaries] = useState({});
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [quoteSymbol, setQuoteSymbol] = useState(null);
  const [quoteData, setQuoteData] = useState(null);
  const [loadingQuote, setLoadingQuote] = useState(false);

  const [filters, setFilters] = useState({
    symbol: '',
    source: '',
    sentiment: '',
    limit: 50,
    offset: 0,
  });
  const [showFilters, setShowFilters] = useState(false);
  const [totalArticles, setTotalArticles] = useState(0);
  const [symbolDraft, setSymbolDraft] = useState('');

  const loadNews = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = {
        limit: filters.limit,
        offset: filters.offset,
      };
      if (filters.symbol) params.symbol = filters.symbol;
      if (filters.source) params.source = filters.source;
      if (filters.sentiment) params.sentiment = filters.sentiment;

      const response = await newsApi.getNews(params);
      setArticles(response.data || []);
      setTotalArticles(response.pagination?.total || 0);
    } catch (err) {
      console.error('Error loading news:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load news');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const loadStats = useCallback(async () => {
    try {
      const response = await newsApi.getStats();
      setStats(response.data);
    } catch (err) {
      console.error('Error loading stats:', err);
    }
  }, []);

  useEffect(() => {
    loadNews();
    loadStats();
  }, [loadNews, loadStats]);

  const flashStatus = (msg) => {
    setStatusMsg(msg);
    window.setTimeout(() => setStatusMsg(null), 4000);
  };

  const handleSync = async () => {
    try {
      setSyncing(true);
      setError(null);
      const result = await newsApi.syncNews({
        limit: 50,
        include_content: true,
        exclude_contentless: true,
      });
      await loadNews();
      await loadStats();
      flashStatus(result.message || 'News sync completed');
    } catch (err) {
      console.error('Error syncing news:', err);
      setError(err.response?.data?.error || err.message || 'Failed to sync news');
    } finally {
      setSyncing(false);
    }
  };

  const handleAnalyzeAll = async () => {
    if (!window.confirm('Analyze sentiment for unanalyzed articles? This may take a while.')) return;
    try {
      setAnalyzing(true);
      setError(null);
      const result = await newsApi.analyzeUnanalyzed(100);
      await loadNews();
      await loadStats();
      flashStatus(
        `Analyzed ${result.data?.analyzed ?? 0} · errors ${result.data?.errors ?? 0}`
      );
    } catch (err) {
      console.error('Error analyzing news:', err);
      setError(err.response?.data?.error || err.message || 'Failed to analyze news');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleAnalyzeArticle = async (articleId) => {
    try {
      await newsApi.analyzeSentiment(articleId);
      await loadNews();
      await loadStats();
    } catch (err) {
      console.error('Error analyzing article:', err);
      setError(err.response?.data?.error || err.message || 'Failed to analyze article');
    }
  };

  const handleSummarizeArticle = async (articleId, forceRegenerate = false) => {
    try {
      setSummarizingArticles((prev) => new Set(prev).add(articleId));
      const result = await newsApi.summarizeArticle(articleId, forceRegenerate);
      setArticleSummaries((prev) => ({
        ...prev,
        [articleId]: {
          summary: result.data.summary,
          cached: result.data.cached,
          generatedAt: result.data.generatedAt,
        },
      }));
      await loadNews();
    } catch (err) {
      console.error('Error summarizing article:', err);
      setError(err.response?.data?.error || err.message || 'Failed to summarize article');
    } finally {
      setSummarizingArticles((prev) => {
        const next = new Set(prev);
        next.delete(articleId);
        return next;
      });
    }
  };

  const handleTickerClick = async (symbol) => {
    try {
      setQuoteSymbol(symbol);
      setShowQuoteModal(true);
      setLoadingQuote(true);
      setQuoteData(null);
      const response = await marketDataApi.getQuote(symbol);
      if (response.success && response.data) setQuoteData(response.data);
      else setQuoteData({ error: 'Failed to load quote' });
    } catch (err) {
      console.error('Error loading quote:', err);
      setQuoteData({
        error: err.response?.data?.error || err.message || 'Failed to load quote',
      });
    } finally {
      setLoadingQuote(false);
    }
  };

  const toggleArticle = (articleId) => {
    setExpandedArticles((prev) => {
      const next = new Set(prev);
      if (next.has(articleId)) next.delete(articleId);
      else next.add(articleId);
      return next;
    });
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      offset: 0,
    }));
  };

  const applySymbolFilter = () => {
    handleFilterChange('symbol', symbolDraft.trim().toUpperCase());
  };

  const clearFilters = () => {
    setSymbolDraft('');
    setFilters({
      symbol: '',
      source: '',
      sentiment: '',
      limit: 50,
      offset: 0,
    });
  };

  const sentimentMix = useMemo(() => {
    const pos = stats?.bySentiment?.positive || 0;
    const neg = stats?.bySentiment?.negative || 0;
    const neu = stats?.bySentiment?.neutral || 0;
    const total = pos + neg + neu || 1;
    return {
      pos,
      neg,
      neu,
      posPct: (pos / total) * 100,
      negPct: (neg / total) * 100,
      neuPct: (neu / total) * 100,
    };
  }, [stats]);

  const pageLabel = `${filters.offset + 1}–${Math.min(
    filters.offset + filters.limit,
    totalArticles
  )} of ${totalArticles}`;

  return (
    <main className="flex-1 container mx-auto px-4 lg:px-6 py-3">
      <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-[var(--lt-text)] flex items-center tracking-tight">
            <FontAwesomeIcon icon={faNewspaper} className="mr-2 text-[var(--lt-action)]" />
            Market News
          </h1>
          <p className="text-xs text-[var(--lt-muted)] mt-1">
            Desk wire · sentiment scoring · AI summaries · click tickers for a quick quote
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setShowFilters((v) => !v)}
            className={`px-2.5 py-1.5 text-xs rounded-md border ${
              showFilters
                ? 'bg-[var(--lt-action-dim)] text-[var(--lt-action)] border-[rgba(108,140,255,0.35)]'
                : 'border-[var(--lt-border)] text-[var(--lt-muted)] hover:bg-[var(--lt-hover)]'
            }`}
          >
            <FontAwesomeIcon icon={faFilter} className="mr-1.5" />
            Filters
          </button>
          <button
            type="button"
            onClick={handleAnalyzeAll}
            disabled={analyzing}
            className="px-2.5 py-1.5 text-xs rounded-md bg-[var(--lt-hover)] text-[var(--lt-text)] border border-[var(--lt-border)] disabled:opacity-50"
          >
            <FontAwesomeIcon
              icon={analyzing ? faSpinner : faBrain}
              className={`mr-1.5 ${analyzing ? 'animate-spin' : ''}`}
            />
            {analyzing ? 'Analyzing…' : 'Analyze'}
          </button>
          <button
            type="button"
            onClick={handleSync}
            disabled={syncing}
            className="px-2.5 py-1.5 text-xs rounded-md bg-[var(--lt-action)] text-white disabled:opacity-50"
          >
            <FontAwesomeIcon
              icon={syncing ? faSpinner : faSync}
              className={`mr-1.5 ${syncing ? 'animate-spin' : ''}`}
            />
            {syncing ? 'Syncing…' : 'Sync'}
          </button>
        </div>
      </div>

      {/* Sentiment strip + stats */}
      <div className="mb-3 rounded-xl border border-[var(--lt-border)] bg-[var(--lt-surface)] overflow-hidden">
        <div className="px-3 py-2.5 flex flex-wrap items-center gap-4">
          <div className="min-w-[160px]">
            <div className="text-[10px] uppercase tracking-wide text-[var(--lt-faint)] mb-1">
              Wire inventory
            </div>
            <div className="font-mono text-lg text-[var(--lt-text)] tabular-nums">
              {stats?.total ?? totalArticles ?? 0}
              <span className="text-xs text-[var(--lt-muted)] ml-1.5 font-sans">articles</span>
            </div>
          </div>

          <div className="flex-1 min-w-[200px]">
            <div className="flex justify-between text-[10px] text-[var(--lt-muted)] mb-1 font-mono tabular-nums">
              <span className="text-[var(--lt-bull)]">{sentimentMix.pos}↑</span>
              <span>{sentimentMix.neu}→</span>
              <span className="text-[var(--lt-bear)]">{sentimentMix.neg}↓</span>
            </div>
            <div className="h-1.5 rounded-full overflow-hidden flex bg-[var(--lt-hover)]">
              <div className="bg-[var(--lt-bull)]" style={{ width: `${sentimentMix.posPct}%` }} />
              <div className="bg-[var(--lt-faint)]" style={{ width: `${sentimentMix.neuPct}%` }} />
              <div className="bg-[var(--lt-bear)]" style={{ width: `${sentimentMix.negPct}%` }} />
            </div>
          </div>

          <div className="flex flex-wrap gap-1">
            {[
              { value: '', label: 'All' },
              { value: 'positive', label: 'Bullish' },
              { value: 'negative', label: 'Bearish' },
              { value: 'neutral', label: 'Neutral' },
            ].map((chip) => (
              <button
                key={chip.value || 'all'}
                type="button"
                onClick={() => handleFilterChange('sentiment', chip.value)}
                className={`px-2 py-1 rounded text-[11px] font-medium transition-colors ${
                  filters.sentiment === chip.value
                    ? 'bg-[var(--lt-action)] text-white'
                    : 'text-[var(--lt-muted)] hover:bg-[var(--lt-hover)] hover:text-[var(--lt-text)]'
                }`}
              >
                {chip.label}
              </button>
            ))}
          </div>

          <form
            className="flex items-center gap-1"
            onSubmit={(e) => {
              e.preventDefault();
              applySymbolFilter();
            }}
          >
            <input
              value={symbolDraft}
              onChange={(e) => setSymbolDraft(e.target.value.toUpperCase())}
              placeholder="SYM"
              className="w-20 px-2 py-1.5 rounded-md bg-[var(--lt-input)] border border-[var(--lt-border)] text-xs font-semibold tracking-wide text-[var(--lt-text)] focus:outline-none focus:border-[var(--lt-action)]"
              spellCheck={false}
            />
            <button
              type="submit"
              className="px-2 py-1.5 text-xs rounded-md bg-[var(--lt-action-dim)] text-[var(--lt-action)]"
            >
              Filter
            </button>
            {(filters.symbol || filters.source || filters.sentiment) && (
              <button
                type="button"
                onClick={clearFilters}
                className="px-2 py-1.5 text-xs text-[var(--lt-muted)] hover:text-[var(--lt-text)]"
              >
                Clear
              </button>
            )}
          </form>
        </div>

        {showFilters && (
          <div className="px-3 py-2.5 border-t border-[var(--lt-border)] bg-[var(--lt-hover)] grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-[10px] uppercase tracking-wide text-[var(--lt-faint)] mb-1">
                Source
              </label>
              <input
                type="text"
                value={filters.source}
                onChange={(e) => handleFilterChange('source', e.target.value)}
                placeholder="e.g. benzinga"
                className="w-full px-2 py-1.5 rounded-md bg-[var(--lt-input)] border border-[var(--lt-border)] text-xs text-[var(--lt-text)]"
              />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-wide text-[var(--lt-faint)] mb-1">
                Page size
              </label>
              <select
                value={filters.limit}
                onChange={(e) => handleFilterChange('limit', parseInt(e.target.value, 10))}
                className="w-full px-2 py-1.5 rounded-md bg-[var(--lt-input)] border border-[var(--lt-border)] text-xs text-[var(--lt-text)]"
              >
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </select>
            </div>
            <div className="flex items-end text-[11px] text-[var(--lt-muted)]">
              Active symbol: <span className="ml-1 font-mono text-[var(--lt-text)]">{filters.symbol || '—'}</span>
            </div>
          </div>
        )}
      </div>

      {(error || statusMsg) && (
        <div
          className={`mb-3 px-3 py-2 rounded-lg border text-xs ${
            error
              ? 'bg-[var(--lt-bear-dim)] border-[rgba(240,113,103,0.35)] text-[var(--lt-bear)]'
              : 'bg-[var(--lt-bull-dim)] border-[rgba(61,220,151,0.35)] text-[var(--lt-bull)]'
          }`}
        >
          {error || statusMsg}
        </div>
      )}

      {/* Wire list */}
      <div className="rounded-xl border border-[var(--lt-border)] bg-[var(--lt-surface)] overflow-hidden">
        {loading ? (
          <div className="py-16 text-center text-[var(--lt-muted)]">
            <FontAwesomeIcon icon={faSpinner} className="animate-spin text-xl mb-2" />
            <div className="text-sm">Loading wire…</div>
          </div>
        ) : articles.length === 0 ? (
          <div className="py-16 text-center text-[var(--lt-muted)]">
            <FontAwesomeIcon icon={faNewspaper} className="text-3xl mb-3 text-[var(--lt-faint)]" />
            <p className="text-sm">No articles match. Sync the wire or clear filters.</p>
          </div>
        ) : (
          <div className="divide-y divide-[var(--lt-border)]">
            {articles.map((article) => {
              const isExpanded = expandedArticles.has(article.id);
              const hasContent = article.content || article.summary;
              const tone = sentimentTone(article.sentiment);
              const aiSummary = articleSummaries[article.id]?.summary || article.aiSummary;

              return (
                <article
                  key={article.id}
                  className="relative px-3 py-3 hover:bg-white/[0.03] transition-colors"
                >
                  <div className={`absolute left-0 top-0 bottom-0 w-0.5 ${tone.bar}`} />

                  <div className="flex flex-wrap items-start gap-3 pl-2">
                    <div className="flex-1 min-w-[220px]">
                      <div className="flex flex-wrap items-center gap-2 mb-1.5 text-[10px] text-[var(--lt-faint)] font-mono tabular-nums">
                        <span title={formatAbsolute(article.createdAt)}>
                          {formatRelative(article.createdAt)}
                        </span>
                        {article.source && (
                          <>
                            <span className="text-[var(--lt-border-strong)]">·</span>
                            <span className="uppercase tracking-wide">{article.source}</span>
                          </>
                        )}
                        {article.author && (
                          <>
                            <span className="text-[var(--lt-border-strong)]">·</span>
                            <span className="font-sans text-[var(--lt-muted)]">{article.author}</span>
                          </>
                        )}
                      </div>

                      <h3 className="text-sm font-semibold text-[var(--lt-text)] leading-snug mb-2">
                        {article.headline}
                      </h3>

                      {article.symbols?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-2">
                          {article.symbols.map((symbol) => (
                            <button
                              key={symbol}
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleTickerClick(symbol);
                              }}
                              className="px-1.5 py-0.5 rounded text-[10px] font-mono font-semibold bg-[var(--lt-action-dim)] text-[var(--lt-action)] hover:brightness-110"
                            >
                              {symbol}
                            </button>
                          ))}
                        </div>
                      )}

                      {article.summary && !isExpanded && (
                        <p className="lt-read-brief text-[var(--lt-muted)] line-clamp-2 mb-2 opacity-90">
                          {article.summary}
                        </p>
                      )}

                      {aiSummary && (
                        <div className="mb-2 rounded-lg border border-[rgba(108,140,255,0.25)] bg-[var(--lt-action-dim)] px-3 py-2.5">
                          <div className="flex items-center justify-between gap-2 mb-1.5">
                            <span className="text-[10px] uppercase tracking-wide text-[var(--lt-action)] font-semibold flex items-center gap-1 font-sans">
                              <FontAwesomeIcon icon={faQuoteLeft} className="text-[9px]" />
                              AI brief
                              {articleSummaries[article.id]?.cached ? ' · cached' : ''}
                            </span>
                            {article.aiSummary && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSummarizeArticle(article.id, true);
                                }}
                                className="text-[10px] text-[var(--lt-action)] hover:underline font-sans"
                              >
                                Regen
                              </button>
                            )}
                          </div>
                          <p className="lt-read-brief">{aiSummary}</p>
                        </div>
                      )}

                      {isExpanded && article.content && (
                        <div className="mt-3 pt-3 border-t border-[var(--lt-border)]">
                          <div
                            className="lt-read"
                            dangerouslySetInnerHTML={{ __html: article.content }}
                          />
                        </div>
                      )}

                      <div className="flex flex-wrap items-center gap-3 mt-1">
                        {hasContent && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleArticle(article.id);
                            }}
                            className="text-[11px] text-[var(--lt-action)] hover:underline flex items-center gap-1"
                          >
                            <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} />
                            {isExpanded ? 'Collapse' : 'Full story'}
                          </button>
                        )}
                        {article.sentimentScore != null && (
                          <span className="text-[10px] font-mono text-[var(--lt-faint)] tabular-nums">
                            score {article.sentimentScore > 0 ? '+' : ''}
                            {Number(article.sentimentScore).toFixed(2)}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex flex-col items-end gap-1.5 shrink-0">
                      {article.sentiment ? (
                        <span
                          className={`px-2 py-0.5 rounded border text-[10px] font-semibold ${tone.chip}`}
                          title={`Score: ${article.sentimentScore ?? 'N/A'}`}
                        >
                          {tone.label}
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAnalyzeArticle(article.id);
                          }}
                          className="px-2 py-0.5 rounded border border-[var(--lt-border)] text-[10px] text-[var(--lt-muted)] hover:text-[var(--lt-text)] hover:bg-[var(--lt-hover)]"
                        >
                          <FontAwesomeIcon icon={faBrain} className="mr-1" />
                          Score
                        </button>
                      )}

                      {summarizingArticles.has(article.id) ? (
                        <span className="px-2 py-0.5 text-[10px] text-[var(--lt-action)]">
                          <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-1" />
                          Brief…
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSummarizeArticle(article.id, false);
                          }}
                          className="px-2 py-0.5 rounded border border-[var(--lt-border)] text-[10px] text-[var(--lt-muted)] hover:text-[var(--lt-text)] hover:bg-[var(--lt-hover)]"
                        >
                          <FontAwesomeIcon icon={faFileAlt} className="mr-1" />
                          {article.aiSummary ? 'Brief' : 'Summarize'}
                        </button>
                      )}

                      {article.url && (
                        <a
                          href={article.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="px-2 py-0.5 text-[10px] text-[var(--lt-action)] hover:underline"
                        >
                          <FontAwesomeIcon icon={faExternalLinkAlt} className="mr-1" />
                          Source
                        </a>
                      )}
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}

        {!loading && articles.length > 0 && (
          <div className="px-3 py-2.5 border-t border-[var(--lt-border)] flex flex-wrap items-center justify-between gap-2 bg-[#10141C]">
            <div className="text-[11px] font-mono text-[var(--lt-muted)] tabular-nums">{pageLabel}</div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() =>
                  setFilters((prev) => ({
                    ...prev,
                    offset: Math.max(0, prev.offset - prev.limit),
                  }))
                }
                disabled={filters.offset === 0}
                className="px-2.5 py-1 text-xs rounded-md border border-[var(--lt-border)] text-[var(--lt-muted)] hover:bg-[var(--lt-hover)] disabled:opacity-40"
              >
                Prev
              </button>
              <button
                type="button"
                onClick={() =>
                  setFilters((prev) => ({
                    ...prev,
                    offset: prev.offset + prev.limit,
                  }))
                }
                disabled={filters.offset + filters.limit >= totalArticles}
                className="px-2.5 py-1 text-xs rounded-md border border-[var(--lt-border)] text-[var(--lt-muted)] hover:bg-[var(--lt-hover)] disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {showQuoteModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
          onClick={() => setShowQuoteModal(false)}
        >
          <div
            className="w-full max-w-sm rounded-xl border border-[var(--lt-border)] bg-[var(--lt-surface)] shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-3 py-2.5 border-b border-[var(--lt-border)] flex items-center justify-between bg-[#10141C]">
              <h3 className="text-sm font-semibold text-[var(--lt-text)]">
                Quote{' '}
                <span className="font-mono text-[var(--lt-action)] tracking-wide">{quoteSymbol}</span>
              </h3>
              <button
                type="button"
                onClick={() => setShowQuoteModal(false)}
                className="text-[var(--lt-faint)] hover:text-[var(--lt-text)]"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="p-3">
              {loadingQuote ? (
                <div className="py-10 text-center text-[var(--lt-muted)]">
                  <FontAwesomeIcon icon={faSpinner} className="animate-spin text-xl" />
                </div>
              ) : quoteData?.error ? (
                <div className="py-6 text-center text-xs text-[var(--lt-bear)]">{quoteData.error}</div>
              ) : quoteData ? (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-2">
                    <div className="rounded-lg bg-[var(--lt-hover)] border border-[var(--lt-border)] p-2.5">
                      <div className="text-[10px] uppercase tracking-wide text-[var(--lt-faint)] mb-1">
                        Bid
                      </div>
                      <div className="font-mono text-lg text-[var(--lt-bull)] tabular-nums">
                        {formatPrice(quoteData.bidPrice)}
                      </div>
                    </div>
                    <div className="rounded-lg bg-[var(--lt-hover)] border border-[var(--lt-border)] p-2.5">
                      <div className="text-[10px] uppercase tracking-wide text-[var(--lt-faint)] mb-1">
                        Ask
                      </div>
                      <div className="font-mono text-lg text-[var(--lt-bear)] tabular-nums">
                        {formatPrice(quoteData.askPrice)}
                      </div>
                    </div>
                  </div>

                  {quoteData.bidPrice && quoteData.askPrice && (
                    <div className="rounded-lg bg-[var(--lt-action-dim)] border border-[rgba(108,140,255,0.25)] p-2.5">
                      <div className="text-[10px] uppercase tracking-wide text-[var(--lt-action)] mb-1">
                        Mid
                      </div>
                      <div className="font-mono text-xl text-[var(--lt-text)] tabular-nums">
                        {formatPrice(
                          (parseFloat(quoteData.bidPrice) + parseFloat(quoteData.askPrice)) / 2
                        )}
                      </div>
                    </div>
                  )}

                  <div className="flex justify-between text-[11px] font-mono text-[var(--lt-muted)] tabular-nums">
                    <span>Bid sz {quoteData.bidSize ?? '—'}</span>
                    <span>Ask sz {quoteData.askSize ?? '—'}</span>
                  </div>

                  {quoteData.timestamp && (
                    <div className="text-[10px] text-[var(--lt-faint)] pt-2 border-t border-[var(--lt-border)]">
                      {formatAbsolute(quoteData.timestamp)}
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

export default NewsPage;
